import { ToolRegistry } from '@/engine/ToolRegistry';
import type { IPipelineStep } from '@/interfaces/IPipeline';

/**
 * Generate an auto-name for a pipeline from its steps.
 * Format: "Tool A → Tool B → Tool C"
 * Returns null if no steps exist.
 */
export function generatePipelineName(steps: IPipelineStep[]): string | null {
  const realSteps = steps.filter((step) => {
    const tool = ToolRegistry.get(step.toolId);
    return tool?.type !== 'visualizer';
  });
  if (realSteps.length === 0) return null;
  const names = realSteps.map((step) => {
    const tool = ToolRegistry.get(step.toolId);
    return step.label || tool?.name || step.toolId;
  });
  return names.join(' → ');
}

/**
 * Generate an auto-description for a pipeline from tool metadata.
 * Derives a summary from tool categories and input/output types.
 * Returns null if no steps exist.
 */
export function generatePipelineDescription(steps: IPipelineStep[]): string | null {
  const realSteps = steps.filter((step) => {
    const tool = ToolRegistry.get(step.toolId);
    return tool?.type !== 'visualizer';
  });
  if (realSteps.length === 0) return null;

  const categories = new Set<string>();
  const toolTypes = new Set<string>();

  for (const step of realSteps) {
    const tool = ToolRegistry.get(step.toolId);
    if (!tool) continue;
    if (tool.category) categories.add(tool.category);
    toolTypes.add(tool.type);
  }

  const categoryList = Array.from(categories);
  const typeList = Array.from(toolTypes);

  if (categoryList.length === 0) return `${steps.length}-step pipeline`;

  const typeLabel = typeList.length === 1 ? typeList[0] : 'pipeline';
  if (categoryList.length === 1) {
    return `${categoryList[0]} ${typeLabel}`;
  }
  return `${categoryList.join(' → ')} pipeline`;
}

/**
 * Reorder pipeline steps so visualizers only appear at head/tail.
 * Mid-pipeline visualizers are moved to the tail.
 */
export function reorderVisualizerStepsRuntime(steps: IPipelineStep[]): IPipelineStep[] {
  const isViz = (s: IPipelineStep) => ToolRegistry.get(s.toolId)?.type === 'visualizer';

  let headEnd = 0;
  while (headEnd < steps.length && isViz(steps[headEnd])) headEnd++;

  let tailStart = steps.length;
  while (tailStart > headEnd && isViz(steps[tailStart - 1])) tailStart--;

  const headVizs = steps.slice(0, headEnd);
  const tailVizs = steps.slice(tailStart);
  const middle = steps.slice(headEnd, tailStart);

  const transforms: IPipelineStep[] = [];
  const midVizs: IPipelineStep[] = [];
  for (const s of middle) {
    if (isViz(s)) {
      midVizs.push(s);
    } else {
      transforms.push(s);
    }
  }

  return [...headVizs, ...transforms, ...midVizs, ...tailVizs];
}
